import { ReactNode } from 'react';

import { Table } from '@tanstack/react-table';
import { t } from 'i18next';
import { ChevronLeft, Info } from 'lucide-react';
import { FieldValues, UseFormReturn } from 'react-hook-form';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { ApiError } from '@/modules/common/api/errors';
import { UnsavedChangesModal } from '@/modules/finance/common/components/unsaved-changes-modal';

import {
  GenerateEntityFilters,
  GenerateEntityFiltersProps,
  GenerateEntityFooter,
  GenerateEntityFooterProps,
  GenerateEntityRefreshButton,
  GenerateEntityRefreshButtonProps,
  GenerateEntityTable,
} from '../components';
import { isGenerateSubRow } from '../utils/generate-entities.utils';

interface GenerateEntityTemplateProps<T, F extends FieldValues>
  extends GenerateEntityFiltersProps,
    Omit<GenerateEntityFooterProps, 'isGenerateDisabled' | 'disabledReason'> {
  /**
   * Replaces the default footer. Screens use it to render a footer that subscribes to the form
   * itself (via `useWatch`) so that validation re-renders only the footer — a page-level
   * subscription would re-render the whole table on every keystroke and field mount.
   */
  footerSlot?: ReactNode;
  /** Used by the default footer only; ignored when `footerSlot` is given. */
  isGenerateDisabled?: boolean;
  /** Used by the default footer only; ignored when `footerSlot` is given. */
  disabledReason?: string;
  displayTitle: string;
  onBack: () => void;
  entityCounter?: string;
  form: UseFormReturn<F>;
  error: Error | null;
  // Modals and overlays
  modals?: ReactNode;
  //   Table Props
  table: Table<T>;
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  handleRowClick?: (entity: T) => void;
  /** Refresh affordance — rendered inline with the filters row */
  refreshButtonProps: GenerateEntityRefreshButtonProps;
  totalCount: number;
  isAnyFilterApplied: boolean;
  tableId?: string;
  onResizeEnd?: () => void;
  unsavedChangesGuard?: {
    isModalOpen: boolean;
    entity: string;
    onDiscard: () => void;
    onCancel: () => void;
  };
  // Date picker only mode
  showDatePickerOnly: boolean;
  /** Heading shown when the date picker is the only thing rendered */
  datePickerPrompt: string;
  /** Selection guidance: text rendered above the table when 0 rows are selected */
  selectionHint?: string;
  /** Hide the selection hint when the table has no rows */
  hasTableRows: boolean;
  /** Number of selected rows (used to toggle the selection-hint alert) */
  selectedItemsCount: number;
  /**
   * Optional table-wide control rendered on the entity-counter row, right-aligned.
   * Used for settings that apply to the whole table rather than a single row
   * (e.g. the bills "bill all eligible as monthly salary" switch).
   */
  toolbarSlot?: ReactNode;
  /**
   * Optional action rendered in the filters row, left of the refresh button — the placement the
   * app uses for "add entity" affordances (see the onboarding reconciliation step). Hidden while
   * only the date picker is shown, since there is no table to add a row to yet.
   */
  filtersActionSlot?: ReactNode;
  /** Ref callback the selection hook uses to scope the one-time pulse hint */
  pulseContainerRef?: (node: HTMLDivElement | null) => void;
}

export const GenerateEntityTemplate = <T, F extends FieldValues>({
  displayTitle,
  onBack,
  handleRowClick,
  hasMore,
  isLoading,
  onLoadMore,
  table,
  modals,
  entityCounter,
  dates,
  selectedPreset,
  onDateChange,
  onPresetSelect,
  onDateReset,
  dateRangeLabel,
  dateRangeInfoTooltip,
  filtersConfig,
  filterState,
  form,
  footerSlot,
  isGenerateDisabled = false,
  onClearClick,
  onGenerateClick,
  selectedItemsCounter,
  submitButtonLabel,
  disabledReason,
  refreshButtonProps,
  error,
  totalCount,
  isAnyFilterApplied,
  tableId,
  onResizeEnd,
  unsavedChangesGuard,
  showDatePickerOnly,
  datePickerPrompt,
  selectionHint,
  hasTableRows,
  selectedItemsCount,
  pulseContainerRef,
  hiddenPresetCategories,
  toolbarSlot,
  filtersActionSlot,
}: GenerateEntityTemplateProps<T, F>) => {
  const filtersRightSlot = (
    <div className="flex items-center gap-2">
      {!showDatePickerOnly && filtersActionSlot}
      <GenerateEntityRefreshButton {...refreshButtonProps} />
    </div>
  );

  return (
    <div className="relative flex h-full flex-col">
      {/* Sticky Header */}
      <div className="shrink-0 border-b border-border bg-surface px-4 py-3 shadow-[0_1px_4px_0_hsl(var(--border))]">
        <div className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 flex-1 items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={onBack}
              className="h-7 w-7 flex-shrink-0 p-0"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <h1 className="truncate text-xl font-semibold leading-none text-foreground sm:text-2xl">
                {displayTitle}
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Content area */}
      <div className="flex flex-1 flex-col overflow-hidden p-6">
        <div className="shrink-0">
          <GenerateEntityFilters
            dates={dates}
            selectedPreset={selectedPreset}
            onDateChange={onDateChange}
            onPresetSelect={onPresetSelect}
            onDateReset={onDateReset}
            dateRangeLabel={dateRangeLabel}
            dateRangeInfoTooltip={dateRangeInfoTooltip}
            filtersConfig={filtersConfig}
            filterState={filterState}
            tableId={tableId}
            rightSlot={filtersRightSlot}
            hiddenPresetCategories={hiddenPresetCategories}
          />

          {!showDatePickerOnly && (entityCounter || toolbarSlot) && (
            <div className="my-4 flex shrink-0 flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">{entityCounter}</p>
              {toolbarSlot}
            </div>
          )}
        </div>

        {showDatePickerOnly && (
          <div className="flex flex-1 items-center justify-center">
            <div className="text-center">
              <h4 className="mb-4 text-lg font-semibold">{datePickerPrompt}</h4>
            </div>
          </div>
        )}

        {!showDatePickerOnly && (
          <Form {...form}>
            <div className="flex min-h-0 flex-1 flex-col" ref={pulseContainerRef}>
              {/* Selection guidance — visible only when there are rows to select but none are selected */}
              {selectionHint && hasTableRows && selectedItemsCount === 0 && (
                <Alert variant="info" className="mb-3 py-2">
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-xs sm:text-sm">
                    {selectionHint}
                  </AlertDescription>
                </Alert>
              )}

              <GenerateEntityTable
                table={table}
                isLoading={isLoading}
                hasMore={hasMore}
                onLoadMore={() => onLoadMore()}
                onRowClick={handleRowClick}
                shouldAnimateRowEnter={isGenerateSubRow}
                error={error instanceof ApiError ? t(error.code) : error?.message}
                isAnyFilterApplied={isAnyFilterApplied}
                totalCount={totalCount ?? 0}
                stickyHeader
                tableId={tableId}
                onResizeEnd={onResizeEnd}
              />
            </div>
          </Form>
        )}
      </div>

      {/* Footer */}
      <div className="shrink-0 border-t border-border bg-surface shadow-[0_-1px_4px_0_hsl(var(--border))]">
        {footerSlot ?? (
          <GenerateEntityFooter
            isGenerateDisabled={isGenerateDisabled}
            onClearClick={onClearClick}
            onGenerateClick={onGenerateClick}
            selectedItemsCounter={selectedItemsCounter}
            selectedItemsCount={selectedItemsCount}
            submitButtonLabel={submitButtonLabel}
            disabledReason={disabledReason}
          />
        )}
      </div>

      {/* Modals and overlays */}
      {modals}

      {/* Built-in unsaved changes confirmation modal */}
      {unsavedChangesGuard && (
        <UnsavedChangesModal
          entity={unsavedChangesGuard.entity}
          open={unsavedChangesGuard.isModalOpen}
          onClose={unsavedChangesGuard.onCancel}
          onDiscard={unsavedChangesGuard.onDiscard}
        />
      )}
    </div>
  );
};
